/**
 * Debug / dev: load game state to a scene index and scroll to scene.
 */

import type { SceneData, ChoiceCondition } from '../scenes/types'
import type { StoryScene } from '../../components/StoryScene'
import { SaveStateManager, type MinigameState } from '../save-state'
import { ITEM_NAMES, ITEMS_NEEDED_TO_PASS } from '../minigames/baleni-s-rychlonozkou/constants'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { scrollManager } from '../../utils/scroll'

function forceUnlockScrollLocks(scene: StoryScene): void {
  const wrapper = scene.getWrapper()
  if (!wrapper) return
  const w = wrapper as any
  if (typeof w.__stickyScrollUnlock === 'function') w.__stickyScrollUnlock()
  if (typeof w.__horizontalScrollUnlock === 'function') w.__horizontalScrollUnlock()
}

export interface LoadGameStateToSceneContext {
  container: HTMLElement
  scenes: SceneData[]
  loadedScenes: Map<string, StoryScene>
  loadScene: (sceneIndex: number, animate: boolean) => void
  setCurrentSceneId: (id: string | null) => void
  saveCurrentState: () => void
  evaluateCondition: (condition: ChoiceCondition) => boolean
}

/**
 * Clear container children except intro. (Story-nav lives under #app, not in game-screen.)
 */
export function clearContainerExceptIntro(container: HTMLElement): void {
  const introElement = container.querySelector('.game-screen__intro')
  const childrenToRemove: Node[] = []
  container.childNodes.forEach((child) => {
    if (child !== introElement) childrenToRemove.push(child)
  })
  childrenToRemove.forEach((child) => {
    try {
      container.removeChild(child)
    } catch {
      // ignore
    }
  })
  void container.offsetHeight
}

/**
 * Load game state to a specific scene index (debug: jump to scene with full progress before it).
 */
export function loadGameStateToScene(
  targetSceneIndex: number,
  ctx: LoadGameStateToSceneContext
): void {
  if (targetSceneIndex < 0 || targetSceneIndex >= ctx.scenes.length) {
    console.error(`Scene index ${targetSceneIndex} out of range`)
    return
  }

  // Find the last scene before target that should be preserved
  let lastPreservedSceneIndex = -1
  for (let i = targetSceneIndex - 1; i >= 0; i--) {
    const sceneData = ctx.scenes[i]
    if (sceneData && sceneData.type !== 'choice') {
      lastPreservedSceneIndex = i
      break
    }
  }

  // Destroy only scenes from targetSceneIndex onwards
  const scenesToDestroy: string[] = []
  ctx.loadedScenes.forEach((scene, sceneId) => {
    const sceneIndex = ctx.scenes.findIndex((s) => s.id === sceneId)
    if (sceneIndex >= targetSceneIndex) {
      scenesToDestroy.push(sceneId)
    }
  })

  scenesToDestroy.forEach((sceneId) => {
    const scene = ctx.loadedScenes.get(sceneId)
    if (scene) {
      scene.destroy()
      ctx.loadedScenes.delete(sceneId)
    }
  })

  // Remove DOM elements for scenes from targetSceneIndex onwards
  // Find the wrapper of the last preserved scene (or intro if no preserved scenes)
  let insertAfterElement: HTMLElement | null = null
  if (lastPreservedSceneIndex >= 0) {
    const lastPreservedSceneData = ctx.scenes[lastPreservedSceneIndex]
    const lastPreservedScene = ctx.loadedScenes.get(lastPreservedSceneData.id)
    if (lastPreservedScene) {
      insertAfterElement = lastPreservedScene.getWrapper()
    }
  }
  
  if (!insertAfterElement) {
    // No preserved scenes, use intro element
    insertAfterElement = ctx.container.querySelector('.game-screen__intro') as HTMLElement
  }

  // Remove all scene-related DOM nodes after the insertAfterElement
  const sceneNodeClasses = ['scene-wrapper', 'scene-spacer', 'sticky-layout-container', 'hscroll-sticky-container', 'epilogue-section', 'epilogue-scene-wrapper']
  if (insertAfterElement) {
    let current: Node | null = insertAfterElement.nextSibling
    const nodesToRemove: Node[] = []
    while (current) {
      if (current.nodeType === Node.ELEMENT_NODE) {
        const element = current as HTMLElement
        if (sceneNodeClasses.some((c) => element.classList.contains(c))) {
          nodesToRemove.push(current)
        }
      }
      current = current.nextSibling
    }
    nodesToRemove.forEach((node) => {
      try {
        ctx.container.removeChild(node)
      } catch {
        // ignore
      }
    })
  } else {
    // Fallback: clear everything except intro
    clearContainerExceptIntro(ctx.container)
  }

  ctx.setCurrentSceneId(null)

  // Load and restore scenes before target (only if they're not already loaded)
  for (let i = 0; i < targetSceneIndex; i++) {
    const sceneData = ctx.scenes[i]
    if (!sceneData) continue
    if (sceneData.type === 'choice') continue
    if (sceneData.when && !ctx.evaluateCondition(sceneData.when)) continue

    // Skip if already loaded
    if (ctx.loadedScenes.has(sceneData.id)) {
      const scene = ctx.loadedScenes.get(sceneData.id)
      if (scene && sceneData.speechBubbles?.length) {
        const lastBubbleIndex = sceneData.speechBubbles.length - 1
        const lastBubbleId = sceneData.speechBubbles[lastBubbleIndex]?.id ?? null
        scene.restoreState(lastBubbleId, lastBubbleIndex)
      }
      if (scene) forceUnlockScrollLocks(scene)
      if (scene && sceneData.choices) {
        setTimeout(() => scene.selectChoice(1), 500)
      }
      continue
    }

    if (sceneData.type === 'minigame' && sceneData.id === 'minihra-baleni-s-rychlonozkou') {
      const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
      const existing = saved.minigames?.[sceneData.id]
      if (!existing?.completed) {
        if (!saved.minigames) saved.minigames = {}
        saved.minigames[sceneData.id] = {
          selectedItems: ITEM_NAMES.slice(0, ITEMS_NEEDED_TO_PASS),
          completed: true,
        }
        SaveStateManager.saveImmediate(saved)
      }
      ctx.loadScene(i, false)
      continue
    }

    ctx.loadScene(i, false)
    const scene = ctx.loadedScenes.get(sceneData.id)
    if (scene && sceneData.speechBubbles?.length) {
      const lastBubbleIndex = sceneData.speechBubbles.length - 1
      const lastBubbleId = sceneData.speechBubbles[lastBubbleIndex]?.id ?? null
      scene.restoreState(lastBubbleId, lastBubbleIndex)
    }
    if (scene) forceUnlockScrollLocks(scene)
    if (scene && sceneData.choices) {
      setTimeout(() => scene.selectChoice(1), 500)
    }
  }

  // Preserve state before target scene, clear only state for scenes after it (and the target scene itself)
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  
  // If the minigame is before the target scene and not yet completed, auto-complete it
  const MINIGAME_ID = 'minihra-baleni-s-rychlonozkou'
  for (let i = 0; i < targetSceneIndex; i++) {
    const sceneData = ctx.scenes[i]
    if (sceneData?.type === 'minigame' && sceneData.id === MINIGAME_ID) {
      const existing = saved.minigames?.[MINIGAME_ID]
      if (!existing?.completed) {
        if (!saved.minigames) saved.minigames = {}
        const selectedItems = ITEM_NAMES.slice(0, ITEMS_NEEDED_TO_PASS)
        saved.minigames[MINIGAME_ID] = {
          selectedItems,
          completed: true,
        }
        if (!saved.inventory) saved.inventory = []
        const consumed = new Set(saved.consumedInventory ?? [])
        selectedItems.forEach((item) => {
          if (!consumed.has(item) && !saved.inventory!.includes(item)) {
            saved.inventory!.push(item)
          }
        })
      }
      break
    }
  }
  
  // Get all scene IDs that come after the target scene AND the target scene itself (these should be cleared)
  const sceneIdsToClear = new Set<string>()
  // Include the target scene itself (in case it has a choice)
  const targetSceneData = ctx.scenes[targetSceneIndex]
  if (targetSceneData) {
    sceneIdsToClear.add(targetSceneData.id)
  }
  // Include all scenes after the target scene
  for (let i = targetSceneIndex + 1; i < ctx.scenes.length; i++) {
    const sceneData = ctx.scenes[i]
    if (sceneData) {
      sceneIdsToClear.add(sceneData.id)
    }
  }
  
  // Filter out choices for scenes after target
  const filteredChoices: Record<string, number> = {}
  if (saved.choices) {
    for (const [sceneId, choiceNumber] of Object.entries(saved.choices)) {
      if (!sceneIdsToClear.has(sceneId)) {
        filteredChoices[sceneId] = choiceNumber
      }
    }
  }
  
  // Filter out minigames for scenes after target (but preserve the auto-completed one)
  const filteredMinigames: Record<string, MinigameState> = {}
  if (saved.minigames) {
    for (const [sceneId, minigameState] of Object.entries(saved.minigames)) {
      if (!sceneIdsToClear.has(sceneId)) {
        filteredMinigames[sceneId] = minigameState
      }
    }
  }
  
  // Compute last bubble state from scenes that were fully restored before target
  let lastBubbleSceneId = targetSceneData?.id ?? ''
  let lastBubbleNumber: number | undefined
  for (let i = targetSceneIndex - 1; i >= 0; i--) {
    const sceneData = ctx.scenes[i]
    if (!sceneData?.speechBubbles?.length) continue
    const scene = ctx.loadedScenes.get(sceneData.id)
    if (!scene) continue
    const lastBubble = scene.getLastUnlockedBubble()
    if (lastBubble !== null) {
      lastBubbleSceneId = sceneData.id
      lastBubbleNumber = lastBubble
      break
    }
  }

  const filteredState: typeof saved = {
    ...saved,
    scrollPosition: window.scrollY,
    choices: filteredChoices,
    minigames: Object.keys(filteredMinigames).length > 0 ? filteredMinigames : undefined,
    inventory: saved.inventory,
    lastLoadedScene: {
      index: targetSceneIndex,
      id: lastBubbleSceneId,
      bubble: lastBubbleNumber,
    },
  }
  
  SaveStateManager.saveImmediate(filteredState)

  if (targetSceneIndex >= 0 && targetSceneIndex < ctx.scenes.length) {
    ctx.loadScene(targetSceneIndex, false)
    ctx.setCurrentSceneId(targetSceneData.id)
  }

  // Reset the chapter-title yellow overlay (may be stale from previous scroll position)
  const chapterOverlay = document.querySelector('.chapter-title-overlay') as HTMLElement | null
  if (chapterOverlay) chapterOverlay.style.opacity = '0'

  // Clear any scroll lock so the user isn't trapped at the old limit.
  // Sticky/horizontal locks set up asynchronously (rAF + 200ms), so unlock repeatedly.
  scrollManager.removeMaxScroll()
  const unlockAllBefore = () => {
    scrollManager.removeMaxScroll()
    for (let i = 0; i < targetSceneIndex; i++) {
      const sd = ctx.scenes[i]
      if (!sd) continue
      const s = ctx.loadedScenes.get(sd.id)
      if (s) forceUnlockScrollLocks(s)
    }
  }
  for (const delay of [200, 500, 1000, 2000]) {
    setTimeout(unlockAllBefore, delay)
  }

  // Refresh ScrollTrigger and background after DOM settles
  const refreshVisuals = () => {
    ScrollTrigger.refresh()
    const bgInst = (window as unknown as { __backgroundTextureInstance?: { update: () => void } }).__backgroundTextureInstance
    if (bgInst?.update) bgInst.update()
  }
  requestAnimationFrame(refreshVisuals)

  // Save state after DOM and scroll settle (bypass isRestoringState guard)
  setTimeout(() => {
    const wrapper = ctx.loadedScenes.get(targetSceneData?.id ?? '')?.getWrapper()
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect()
      const scrollY = window.scrollY + rect.top
      filteredState.scrollPosition = Math.max(0, scrollY)
    } else {
      filteredState.scrollPosition = window.scrollY
    }
    SaveStateManager.saveImmediate(filteredState)
  }, 500)
}

export interface ScrollToSceneContext {
  container: HTMLElement
  scenes: SceneData[]
  loadedScenes: Map<string, StoryScene>
}

function waitForImages(container: HTMLElement): Promise<void> {
  const images = Array.from(container.querySelectorAll('img'))
  const pending = images.filter((img) => !img.complete)
  if (pending.length === 0) return Promise.resolve()
  return new Promise((resolve) => {
    let remaining = pending.length
    const onDone = () => {
      remaining--
      if (remaining <= 0) resolve()
    }
    for (const img of pending) {
      img.addEventListener('load', onDone, { once: true })
      img.addEventListener('error', onDone, { once: true })
    }
  })
}

/**
 * Scroll the page so the given scene is centered in the viewport.
 * Waits for all images in the container to load so heights are final.
 */
export function scrollToScene(
  sceneIndex: number,
  ctx: ScrollToSceneContext
): void {
  if (sceneIndex < 0 || sceneIndex >= ctx.scenes.length) return
  const sceneData = ctx.scenes[sceneIndex]
  const scene = ctx.loadedScenes.get(sceneData.id)
  const wrapper = scene?.getWrapper()
  if (!wrapper) return

  const doScroll = () => {
    const lenis = (window as unknown as { __lenis?: { scroll: number; scrollTo: (y: number, opts?: { duration?: number; easing?: (t: number) => number }) => void } }).__lenis
    if (!lenis || typeof lenis.scrollTo !== 'function') return
    const rect = wrapper.getBoundingClientRect()
    const currentScrollY = lenis.scroll ?? window.scrollY
    const viewportHeight = window.innerHeight
    const targetScrollY = currentScrollY + rect.top - viewportHeight / 2 + rect.height / 2
    lenis.scrollTo(Math.max(0, targetScrollY), { duration: 0 })
  }

  const refreshVisuals = () => {
    ScrollTrigger.refresh()
    const bg = (window as unknown as { __backgroundTextureInstance?: { update: () => void } }).__backgroundTextureInstance
    if (bg?.update) bg.update()
  }

  requestAnimationFrame(() => {
    doScroll()
    requestAnimationFrame(refreshVisuals)
  })

  // Re-scroll after images have loaded and after sticky containers settle
  const RESNAP_DELAYS = [500, 1500, 3000]
  for (const delay of RESNAP_DELAYS) {
    setTimeout(() => {
      doScroll()
      refreshVisuals()
    }, delay)
  }
}
